import {
  FiFileText,
  FiMapPin,
  FiShield,
  FiSmartphone,
  FiUser,
  FiUsers,
  FiBriefcase,
} from "react-icons/fi";
import { autoFormatDocument } from "../../../Utils/formatters";
import { handleBussinessTaxIDInput } from "../../../Utils/masks";
import { birthDateToIso } from "../../../Utils/masks";

export const BRAZILIAN_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export const PF_STEPS = [
  { id: "personal", label: "Dados pessoais", icon: FiUser },
  { id: "address", label: "Endereço", icon: FiMapPin },
  { id: "documents", label: "Documentação", icon: FiShield },
  { id: "liveness", label: "Liveness", icon: FiSmartphone },
  { id: "review", label: "Confirmação", icon: FiFileText },
];

export const PJ_STEPS = [
  { id: "company", label: "Dados da empresa", icon: FiBriefcase },
  { id: "owners", label: "Beneficiários", icon: FiUsers },
  { id: "documents", label: "Documentação", icon: FiShield },
  { id: "liveness", label: "Liveness", icon: FiSmartphone },
  { id: "review", label: "Confirmação", icon: FiFileText },
];

export const EMPTY_ADDRESS = {
  zipCode: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
};

export const EMPTY_PERSONAL = {
  fullName: "",
  document: "",
  birthDate: "",
  email: "",
  phone: "",
};

export const EMPTY_COMPANY = {
  businessName: "",
  cnpj: "",
  address: { ...EMPTY_ADDRESS },
};

export const createEmptyOwner = () => ({
  firstName: "",
  lastName: "",
  birthDate: "",
  email: "",
  phone: "",
  cpf: "",
  isControlPerson: false,
  address: { ...EMPTY_ADDRESS },
});

export const EMPTY_LIVENESS = {
  sessionId: null,
  qrCodeUrl: null,
  status: "idle",
};

export const EMPTY_DOCUMENTS = {
  addressProof: null,
  socialContract: null,
  sourceOfFunds: null,
};

const COMPANY_NAME_HINTS = /\b(LTDA\.?|S\.?\/?A\.?|ME|EPP|EIRELI|SOCIEDADE)\b/i;

export const isCnpjAccount = (accountData = {}) => {
  const type = (accountData.userDocumentType || "").toUpperCase();
  if (type.includes("CNPJ")) return true;

  const digits = (accountData.userDocumentRaw || accountData.userDocument || "").replace(
    /\D/g,
    ""
  );
  if (digits.length > 11) return true;

  return COMPANY_NAME_HINTS.test(accountData.userName || "");
};

export const resolveAccountDocument = (accountData = {}) => {
  if (accountData.userDocument?.trim()) {
    return accountData.userDocument;
  }

  const raw = (accountData.userDocumentRaw || "").replace(/\D/g, "");
  if (!raw) return "";

  return autoFormatDocument(raw);
};

export const getDocumentDigits = (value) => (value || "").replace(/\D/g, "");

export const toE164Phone = (phone) => {
  const digits = getDocumentDigits(phone);
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return `+${digits}`;
  return `+55${digits}`;
};

export const buildAddressLine1 = ({ street, number, complement }) => {
  let line = (street || "").trim();
  if (number?.trim()) line = line ? `${line}, ${number.trim()}` : number.trim();
  if (complement?.trim()) line = line ? `${line} - ${complement.trim()}` : complement.trim();
  return line;
};

export const formatPostalCode = (zipCode) => {
  const digits = getDocumentDigits(zipCode);
  if (digits.length !== 8) return zipCode;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const mapAddressToApi = (address) => ({
  line_1: buildAddressLine1(address),
  city: address.city.trim(),
  state: address.state,
  postal_code: formatPostalCode(address.zipCode),
  country_code: "BR",
});

export const buildBusinessDetailsPayload = ({ company, beneficialOwners }) => ({
  business_name: company.businessName.trim(),
  registration_id: {
    number: getDocumentDigits(company.cnpj),
    country_code: "BR",
  },
  address: mapAddressToApi(company.address),
  beneficial_owners: beneficialOwners.map((owner) => ({
    first_name: owner.firstName.trim(),
    last_name: owner.lastName.trim(),
    date_of_birth: birthDateToIso(owner.birthDate),
    email: owner.email.trim(),
    phone_number: toE164Phone(owner.phone),
    national_id: {
      number: getDocumentDigits(owner.cpf),
      country_code: "BR",
    },
    address: mapAddressToApi(owner.address),
    is_control_person: Boolean(owner.isControlPerson),
  })),
});

export const buildInitialPersonal = (accountData = {}) => {
  const companyAccount = isCnpjAccount(accountData);
  const documentValue = resolveAccountDocument(accountData);

  return {
    fullName: accountData.userName || "",
    document: documentValue
      ? companyAccount
        ? handleBussinessTaxIDInput(documentValue)
        : documentValue
      : "",
    birthDate: "",
    email: accountData.userEmail || "",
    phone: accountData.userPhone || "",
  };
};

export const buildInitialCompany = (accountData = {}) => {
  const documentValue = resolveAccountDocument(accountData);

  return {
    businessName: accountData.userName || "",
    cnpj: documentValue ? handleBussinessTaxIDInput(documentValue) : "",
    address: { ...EMPTY_ADDRESS },
  };
};

export const buildInitialOwners = (accountData = {}) => {
  const owner = createEmptyOwner();
  owner.email = accountData.userEmail || "";
  owner.phone = accountData.userPhone || "";
  owner.isControlPerson = true;
  return [owner];
};

export const resolveLivenessSubject = ({ isCompanyAccount, personal, company, beneficialOwners }) => {
  if (!isCompanyAccount) return personal;

  const controlPerson =
    beneficialOwners.find((owner) => owner.isControlPerson) || beneficialOwners[0];

  if (!controlPerson) {
    return {
      fullName: company.businessName,
      document: company.cnpj,
      email: "",
      phone: "",
    };
  }

  return {
    fullName: `${controlPerson.firstName} ${controlPerson.lastName}`.trim(),
    document: controlPerson.cpf,
    email: controlPerson.email,
    phone: controlPerson.phone,
  };
};
